/**
 * JWT Token utilities for admin authentication
 */

/**
 * Decode JWT token without verification (client-side only for expiry check)
 * @param {string} token - JWT token
 * @returns {object|null} - Decoded payload or null if invalid
 */
export function decodeJWT(token) {
    if (!token) return null;
    
    try {
        const base64Url = token.split('.')[1];
        if (!base64Url) return null;
        
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        
        return JSON.parse(jsonPayload);
    } catch (error) {
        console.warn('Failed to decode JWT token:', error);
        return null;
    }
}

/**
 * Check if JWT token is expired
 * @param {string} token - JWT token
 * @returns {boolean} - True if token is expired
 */
export function isTokenExpired(token) {
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) {
        return true; // Consider invalid tokens as expired
    }
    
    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp < currentTime;
}

/**
 * Get token expiration time
 * @param {string} token - JWT token
 * @returns {Date|null} - Expiration date or null if invalid
 */
export function getTokenExpiration(token) {
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) {
        return null;
    }
    
    return new Date(decoded.exp * 1000);
}

/**
 * Get time until token expires in seconds
 * @param {string} token - JWT token
 * @returns {number} - Seconds until expiration, or 0 if expired/invalid
 */
export function getTimeUntilExpiry(token) {
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) {
        return 0;
    }
    
    const currentTime = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = decoded.exp - currentTime;
    return Math.max(0, timeUntilExpiry);
}